const post = {
  slug: "write-product-descriptions-instagram-sellers",
  title: "How to Write Product Descriptions That Sell on Instagram",
  seoTitle: "Product Descriptions That Sell on Instagram",
  description:
    "Learn how to write short, clear product descriptions for Instagram: what to include, a simple template, before and after examples and mistakes that cost sales.",
  excerpt:
    "On Instagram, customers decide in seconds. A good product description answers their questions before they ask. Here is what to include, a template and examples you can copy.",
  category: "Selling on Instagram",
  tags: ["Product descriptions for Instagram", "Instagram product captions", "Instagram catalog"],
  date: "2026-11-02",
  cover: {
    src: "/blog-images/write-product-descriptions-instagram-sellers-cover.png",
    alt: "A phone showing an Instagram DM with a clear product description and price",
  },
  blocks: [
    {
      type: "p",
      text:
        "A photo gets attention, but a description gets the sale. When a customer sees your product on Instagram, they have the same questions every time. What is it made of? Does it come in my size? How much is it? When will it arrive? If your description answers those, you save a round of messages. If it does not, many people simply scroll on.",
    },
    {
      type: "p",
      text:
        "Descriptions on Instagram are different from descriptions on a website. They are shorter, they are read on a phone and they often show up inside a chat rather than on a page. This guide shows what to include and how to keep it clear.",
    },

    { type: "h2", text: "What every description should answer" },
    {
      type: "ul",
      items: [
        "**What it is.** Name the product plainly, such as \u201Clinen shirt\u201D rather than a clever name nobody will search for.",
        "**What it is made of.** Fabric, material or file type. Customers care and it builds trust.",
        "**Who it is for and when to use it.** \u201CLight enough for summer\u201D or \u201Cplanner for freelancers\u201D helps people picture themselves with it.",
        "**Size, colour or options.** List what is available, and keep it accurate.",
        "**The price.** Put it in the description or the first reply, so nobody has to ask.",
        "**What happens next.** How to order, how long delivery takes and what your return rule is.",
      ],
    },

    { type: "h2", text: "A simple template" },
    {
      type: "p",
      text:
        "You do not need a new structure for every product. Use the same one every time, so customers learn where to find things.",
    },
    {
      type: "quote",
      text:
        "[Product name] in [material]. [One line on why it is good or who it suits]. Sizes: [list]. Colour: [list]. Price: \u20B9[amount], free delivery above \u20B9[amount]. Delivery in [X] days. Exchanges within [X] days.",
    },
    {
      type: "p",
      text:
        "The first line says what it is, the second says why anyone should care, and the rest answers the practical questions. That is the whole formula.",
    },

    { type: "h2", text: "Before and after" },
    {
      type: "p",
      text:
        "**Before:** \u201CBeautiful shirt, DM to order \u2764\uFE0F\u201D",
    },
    {
      type: "p",
      text:
        "**After:** \u201CLinen shirt in soft, breathable fabric. Easy to wear to work or out. Sizes S, M, L. Colours: sage and sand. \u20B91,299, free delivery above \u20B91,500. Ships in 2 days. Exchange within 7 days.\u201D",
    },
    {
      type: "p",
      text:
        "The second version is only slightly longer, but it removes five questions the customer would otherwise have to ask.",
    },

    { type: "h2", text: "Write for a phone screen" },
    {
      type: "ul",
      items: [
        "**Lead with what matters.** The first line is what most people read. Put the product and the benefit there.",
        "**Use short lines.** Break up long blocks. One idea per line is easy to read on a small screen.",
        "**Keep sentences plain.** \u201CWashes well in a machine\u201D beats \u201Cexceptional laundering performance.\u201D",
        "**Avoid walls of emoji.** One or two for rhythm are fine. A row of them hides the information.",
        "**Skip empty praise.** \u201CAmazing quality\u201D means nothing. \u201CDouble-stitched seams\u201D means something.",
      ],
    },

    { type: "h2", text: "Describe size and fit honestly" },
    {
      type: "p",
      text:
        "Wrong-size orders are one of the biggest causes of returns for clothing and accessories. Give real measurements, not just S, M and L. For a shirt, include chest and length in inches or centimetres. Mention the model\u2019s height and the size they wear. If something runs small, say so. An honest description costs you a few hesitant buyers and saves you many returns.",
    },
    {
      type: "p",
      text:
        "Checkout DM\u2019s public catalog page supports an extended description, a sizing guide, care instructions and FAQs for each product, so the details sit with the product instead of in a pile of old messages. See the [pricing page](/pricing) for which plans include them, and our guide to [Instagram catalog photos and sizes](/blog/instagram-catalog-tips-photos-sizes) for how to set the rest up.",
    },

    { type: "h2", text: "Write for digital products too" },
    {
      type: "p",
      text:
        "A digital product needs a slightly different description. Say what the file is, how many pages or items it has, the format, what the customer can do with it and how they receive it. \u201C40-page PDF planner, delivered as a download link right after payment\u201D is clear. Our guide to [selling digital products on Instagram](/blog/sell-digital-products-on-instagram) has more examples.",
    },

    { type: "h2", text: "Mistakes that cost sales" },
    {
      type: "ul",
      items: [
        "**Hiding the price.** \u201CDM for price\u201D makes many people leave. Show it, or give it in your first reply.",
        "**Leaving out delivery and returns.** Buyers hesitate when they do not know what happens if something goes wrong. See [writing a return policy](/blog/return-policy-for-instagram-shop).",
        "**Different details in different places.** If the post says \u20B9999 and the chat says \u20B91,099, you lose trust. Keep one source of truth.",
        "**Overpromising.** Words like \u201Cperfect for every body\u201D lead to returns. Describe what the product is.",
        "**Never updating.** A description that lists sold-out sizes frustrates buyers. Keep stock current.",
      ],
    },

    { type: "h2", text: "Keep your replies consistent" },
    {
      type: "p",
      text:
        "The same information should appear in your posts, your catalog and your replies. If you answer \u201Cprice?\u201D and \u201Cis it available?\u201D many times a day, use short saved replies built from the same template. Our [Instagram DM reply templates](/blog/instagram-dm-reply-templates-for-sellers) give you 15 you can adapt.",
    },
    {
      type: "chat",
      shop: "yourshop",
      title: "A description doing its job",
      caption: "Illustrative example. Use your own products and wording.",
      messages: [
        { from: "buyer", text: "Tell me about the linen shirt" },
        { from: "shop", text: "It is made of soft, breathable linen, good for summer. Sizes S, M, L in sage or sand. \u20B91,299 with free delivery above \u20B91,500. Which size are you?" },
        { from: "buyer", text: "M in sage" },
        { from: "shop", text: "Great choice! M in sage is in stock. Shall I send the payment link?" },
      ],
    },
    {
      type: "callout",
      tone: "peach",
      title: "Test your own description",
      text:
        "Show your description to a friend and ask what questions they still have. Every question they ask is something to add. When nobody has questions left, your description is ready.",
    },
  ],
  related: [
    { to: "/catalog-guide", title: "Catalog Guide", description: "Product cards, stock, sizes and in-DM search." },
    { to: "/pricing", title: "Pricing", description: "Compare plans, from the Free plan to Pro." },
    { to: "/faq", title: "Frequently Asked Questions", description: "Answers on payments, shipping and returns." },
  ],
  faqs: [
    {
      q: "How long should an Instagram product description be?",
      a: "Short enough to read on a phone in a few seconds, but long enough to answer the basic questions. Most products need a few lines covering what it is, the material, the sizes or options, the price, delivery and the return rule.",
    },
    {
      q: "Should I put the price in the description?",
      a: "Yes, when you can. Showing the price upfront saves a round of messages and filters out people who were never going to buy, so your replies go to serious buyers.",
    },
    {
      q: "What should I include for clothing?",
      a: "Fabric, fit, available sizes and colours, real measurements, the model\u2019s height and size, care instructions, price, delivery time and your exchange rule.",
    },
    {
      q: "Can I add sizing guides and care instructions in Checkout DM?",
      a: "Yes. The public catalog page supports an extended description, a sizing guide, care instructions and FAQs for each product. Check the pricing page for which plans include them.",
    },
  ],
};

export default post;
